import abilities from "./tables/abilities.json";
import backgrounds from "./tables/backgrounds.json";
import constants from "./tables/constants.json";
import creatureLevels from "./tables/creature_levels.json";
import crits from "./tables/crits.json";
import effects from "./tables/effects.json";
import items from "./tables/items.json";
import levels from "./tables/levels.json";
import looks from "./tables/looks.json";
import resources from "./tables/resources.json";
import weapons from "./tables/weapons.json";
import xpGap from "./tables/xp_gap.json";
import { indexLooks } from "./looks.ts";
import type { Ability, Effect, Rules, RulesProblem, Weapon } from "./types.ts";
import { describeProblem, validateRules } from "./validate.ts";
import { rulesVersion } from "./version.ts";

/**
 * The rules loader (ADR-069 decision 6): the only code that imports a rules table, which the boundary lint's
 * `rules-tables-only-through-loader` holds to. The map server and the page each read the rules through it once,
 * and a later loader that reads the same documents from a database changes only where `loadRules` finds them.
 */

/**
 * A set of rules tables the validator refused, with every problem it named.
 */
export class RulesError extends Error {
  /**
   * @param problems Every problem, each naming its table and its record's id.
   */
  public constructor(public readonly problems: readonly RulesProblem[]) {
    super(`the rules tables are invalid:\n${problems.map((problem) => describeProblem(problem)).join("\n")}`);
    this.name = "RulesError";
  }
}

/**
 * Freezes a value and everything inside it, lists and records alike.
 *
 * @param value JSON data built by the validator.
 * @returns The same value, frozen all the way down.
 */
function freezeDeep<T>(value: T): T {
  if (typeof value === "object" && value !== null) {
    for (const inner of Object.values(value)) {
      freezeDeep(inner);
    }
    Object.freeze(value);
  }
  return value;
}

/**
 * Reads a set of rules tables: validates them, freezes the tables the validator built, never the documents it
 * was handed, indexes their records and names their version.
 *
 * @param documents The twelve tables, as a record of lists.
 * @returns The rules.
 * @throws RulesError when the validator refuses the tables, listing every problem.
 * @example
 * const rules = readRules(documentsFromADatabase);
 * rules.abilities.get("strike")?.cooldownTicks;
 */
export function readRules(documents: unknown): Rules {
  const verdict = validateRules(documents);
  if (!verdict.ok) {
    throw new RulesError(verdict.problems);
  }
  const tables = freezeDeep(verdict.tables);
  const startingWeapon = tables.weapons.find((weapon) => weapon.starting) as Weapon;
  return Object.freeze({
    version: rulesVersion(tables),
    tables,
    abilities: new Map(tables.abilities.map((ability) => [ability.id, ability])),
    items: new Map(tables.items.map((item) => [item.id, item])),
    weapons: new Map(tables.weapons.map((weapon) => [weapon.id, weapon])),
    effects: new Map(tables.effects.map((effect) => [effect.id, effect])),
    resources: Object.freeze(Object.fromEntries(tables.resources.map((resource) => [resource.id, resource]))) as Rules["resources"],
    constants: Object.freeze(Object.fromEntries(tables.constants.map((constant) => [constant.id, "ticks" in constant ? constant.ticks : constant.count]))) as Rules["constants"],
    crits: Object.freeze(Object.fromEntries(tables.crits.map((crit) => [crit.id, crit]))) as Rules["crits"],
    looks: indexLooks(tables.looks),
    startingWeapon,
  });
}

/**
 * Loads the rules tables shipped with this build: the twelve JSON documents under `rules/tables/`, through
 * `readRules`. Called once as the map server is made and once as the page starts.
 *
 * @returns The build's rules.
 * @throws RulesError when the build's tables are invalid, which `bun test` catches before a build ships.
 * @example
 * loadRules().version;
 * // "r-" and 8 hex digits, the value `welcome.rulesVersion` carries
 */
export function loadRules(): Rules {
  return readRules({ abilities, backgrounds, constants, creature_levels: creatureLevels, crits, effects, items, levels, looks, resources, weapons, xp_gap: xpGap });
}

/**
 * An ability's number on the wire (ADR-073 decision 16): its place in the abilities table, counting from 1, which
 * `act`, the combat events and the crit roll carry. The rules version names the tables, so a page and a map server of
 * one version agree on every number.
 *
 * @param rules The rules.
 * @param id The ability's id.
 * @returns Its number, 1 or more.
 * @throws Error when the rules hold no ability of the id.
 */
export function abilityNumber(rules: Rules, id: string): number {
  const index = rules.tables.abilities.findIndex((ability) => ability.id === id);
  if (index < 0) {
    throw new Error(`the rules hold no ability ${id}`);
  }
  return index + 1;
}

/**
 * The ability a wire number names.
 *
 * @param rules The rules.
 * @param number The number from an `act`, 1 or more.
 * @returns The ability, or undefined for 0, a number past the table or one that is not whole.
 */
export function abilityByNumber(rules: Rules, number: number): Ability | undefined {
  return Number.isInteger(number) && number >= 1 ? rules.tables.abilities[number - 1] : undefined;
}

/**
 * An effect's number on the wire (ADR-079 decision 1): its place in the effects table, counting from 1, which the
 * Buffs component and the owner's `effects` frame carry, as an ability's number is its place in the abilities table.
 *
 * @param rules The rules.
 * @param id The effect's id.
 * @returns Its number, 1 or more.
 * @throws Error when the rules hold no effect of the id.
 */
export function effectNumber(rules: Rules, id: string): number {
  const index = rules.tables.effects.findIndex((effect) => effect.id === id);
  if (index < 0) {
    throw new Error(`the rules hold no effect ${id}`);
  }
  return index + 1;
}

/**
 * The effect a wire number names.
 *
 * @param rules The rules.
 * @param number The number from the Buffs component or an `effects` frame, 1 or more.
 * @returns The effect, or undefined for 0, a number past the table or one that is not whole.
 */
export function effectByNumber(rules: Rules, number: number): Effect | undefined {
  return Number.isInteger(number) && number >= 1 ? rules.tables.effects[number - 1] : undefined;
}
